import React from "react";
import { Plus, X } from "lucide-react";
import ClockField from "./ClockField.jsx";
import { INPUT } from "./Fields.jsx";
import { addBreak, breakComplete, removeBreak, sheetBreaks, updateBreak } from "../../utils/consolidationBreaks.js";

const MISSING = "rounded-[4px] [&_input]:!border-amber-400 [&_input]:!ring-2 [&_input]:!ring-amber-200";
const PAID = { "": null, paid: true, unpaid: false };

// Champs des pauses d'un relevé ou d'une soirée. Chaque pause : début, fin, payée ou non au chauffeur (choix obligatoire),
// retrait ; « + Pause » en ajoute une. fields : avec libellés ; inline : sur une ligne du relevé de tournée, les heures (clocks)
// dans la colonne des heures, le choix et le retrait (extras) à gauche.
// onChange(update) : update(row courant) donne la nouvelle liste.
export function breakControls({ row, timezone = row.timezone, baseDate = row.dispatch_date, editable, markMissing = false, nextDayBefore = null, onChange }) {
  const pauses = sheetBreaks(row);
  const suffix = index => index ? ` ${index + 1}` : "";
  const clock = (index, edge, hideLabel) => {
    const pause = pauses[index], label = `${edge === "start" ? "Début" : "Fin"} pause${suffix(index)}`;
    const missing = markMissing && !pause[edge] && Boolean(pause.start) !== Boolean(pause.end);
    return <span data-consolidation-missing={missing || undefined} className={missing ? MISSING : "contents"}>
      <ClockField label={label} hideLabel={hideLabel} value={pause[edge]} baseDate={baseDate} nextDayBefore={nextDayBefore} timezone={timezone} disabled={!editable}
        onChange={value => onChange(current => updateBreak(current, index, { [edge]: value }))} /></span>;
  };
  const paid = index => {
    const pause = pauses[index], missing = markMissing && breakComplete(pause) && pause.paid == null;
    return <select aria-label={`Pause${suffix(index)} payée au chauffeur ?`} title="Jamais facturée à l’entreprise ; payée ou non au chauffeur"
      data-consolidation-missing={missing || undefined} disabled={!editable}
      value={pause.paid == null ? "" : pause.paid ? "paid" : "unpaid"} onChange={event => onChange(current => updateBreak(current, index, { paid: PAID[event.target.value] }))}
      className={`${INPUT} !h-6 !w-auto !py-0 !pl-1 !pr-6 text-xs ${missing ? "!border-amber-400 !ring-2 !ring-amber-200" : ""}`}>
      <option value="">Payée ?</option><option value="paid">Payée</option><option value="unpaid">Non payée</option>
    </select>;
  };
  const remove = index => editable ? <button type="button" aria-label={`Retirer la pause${suffix(index)}`} title="Retirer la pause"
    onClick={() => onChange(current => removeBreak(current, index))}
    className="inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-slate-500 hover:bg-slate-100 hover:text-red-700 focus-visible:outline-2 focus-visible:outline-accent">
    <X size={13} strokeWidth={2.5} aria-hidden="true" /></button> : null;
  const add = editable ? <button type="button" title="Ajouter une pause : jamais facturée, payée ou non au chauffeur"
    onClick={event => {
      const scope = event.currentTarget.closest("article, section") || document, label = `Début pause${suffix(pauses.length)}`;
      onChange(addBreak);
      requestAnimationFrame(() => requestAnimationFrame(() => scope.querySelector(`input[aria-label="${label}"]`)?.focus()));
    }}
    className="inline-flex h-6 items-center gap-1 rounded-[4px] border border-dashed border-slate-400 px-1.5 text-[11.5px] font-semibold text-slate-700 hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-accent">
    <Plus size={12} strokeWidth={2.5} aria-hidden="true" />Pause</button> : null;
  return {
    fields: pauses.map((_, index) => <>
      <div className="grid grid-cols-2 items-end gap-x-3 text-right [&>span>div]:w-[5.25rem] [&_div.flex]:justify-end">{clock(index, "start", false)}{clock(index, "end", false)}</div>
      {paid(index)}{remove(index)}</>),
    inline: pauses.map((_, index) => ({ clocks: <span className="inline-flex items-center gap-1.5">{clock(index, "start", true)}
      <span aria-hidden="true" className="text-slate-500">→</span>{clock(index, "end", true)}</span>, extras: <>{paid(index)}{remove(index)}</> })),
    add,
  };
}
